import { getActiveOffer } from '@/components/discountLanding/utils/getActiveOffer';
import Purchase from '@/hooks/purchase/purchase';
import { Device } from '@/store/device/types';
import { Prices } from '@/store/prices/types';
import { PurchaseParams, RouteSpecificPurchaseParams } from '@/store/purchaseParams/types';
import { User } from '@/store/user/types';
import getParamAsString from '@/utils/getParamsAsString';
import isFirstMonthCoupon from '@/utils/prices/isFirstMonthCoupon';

import { userCanTrial } from '../subscriptions';

export const oneTimePurchasePlans = [] as const;
const validPlans = ['monthly', 'yearly', 'quarterly', 'family', 'lifetime', ...oneTimePurchasePlans] as const;
export type ValidPlan = (typeof validPlans)[number];
export const isValidPlan = (value: string): value is ValidPlan => validPlans.includes(value as ValidPlan);

export type PurchaseQueryParams = {
	plan?: string | null;
	trialLength?: string | null;
	utm_medium?: string | null;
	coupon?: string | null;
	promotion?: string | null;
	guestPass?: string | null;
	calmjwt?: string | null;
	is30DayOverride?: string | null;
};

function getPlan(plan: string | null, isFirstMonthOffer: boolean): ValidPlan {
	if (plan && isValidPlan(plan)) {
		return plan;
	}
	// The first-month coupon only discounts the monthly price, so a yearly default would charge
	// the full annual price while the offer page shows the first month.
	return isFirstMonthOffer ? 'monthly' : 'yearly';
}

const getPromotion = (promotion: string | null, isPaidTraffic: boolean, isTrialFlow: boolean) => {
	if (isPaidTraffic && isTrialFlow) {
		return '7_day_free_trial';
	}

	return promotion;
};

const initCouponValue = ({
	plan,
	coupon,
	prices,
}: {
	plan: ValidPlan;
	coupon?: string | null;
	prices: Prices | null;
}): string | null => prices?.coupons[plan]?.id || getParamAsString(coupon) || null;

// The first-month coupon is only an offer under US pricing and only discounts the monthly plan.
// Any other pairing must not reach checkout from any source, including a route-forwarded coupon.
const withoutUnofferedFirstMonth = ({
	coupon,
	prices,
	isFirstMonthOffer,
	plan,
}: {
	coupon: string | null;
	prices: Prices | null;
	isFirstMonthOffer: boolean;
	plan: ValidPlan;
}) => {
	const monthly = prices?.coupons?.monthly;
	const isFirstMonth = isFirstMonthCoupon(monthly) && coupon === monthly.id;
	return isFirstMonth && (!isFirstMonthOffer || plan !== 'monthly') ? null : coupon;
};

export function initPurchaseParams({
	query,
	prices,
	user,
	device,
	routeSpecificPurchaseParams,
}: {
	query: PurchaseQueryParams;
	prices: Prices | null;
	user: User | null;
	device: Device | null | undefined;
	routeSpecificPurchaseParams?: RouteSpecificPurchaseParams | null;
}): PurchaseParams {
	const isFirstMonthOffer = getActiveOffer(prices, device)?.type === 'firstMonth';
	// At SSR only a family route may pick the family plan. A bare ?plan=family reaches pages that show
	// yearly trial copy and totals, so the session would charge a plan the buyer was never shown.
	// The plan toggle picks family on the client when its experiment is enrolled.
	const queryPlan = getParamAsString(query.plan);
	const plan = getPlan(
		routeSpecificPurchaseParams?.plan || (queryPlan === 'family' ? null : queryPlan),
		isFirstMonthOffer,
	);
	const customTrialLength = Number(getParamAsString(query.trialLength));
	const trialLengthInt = Number.isNaN(customTrialLength) ? null : customTrialLength;
	const isPaidTraffic = query.utm_medium === 'paid';

	// this trial determination logic is brutal - need to reduce to a single source of truth
	const noTrial = !userCanTrial(user) && query.utm_medium !== 'second_free_trial';
	const isTrialFlow = Boolean(routeSpecificPurchaseParams?.hasFreeTrial && !noTrial);

	const initialPurchaseParams: PurchaseParams = {
		plan,
		coupon: withoutUnofferedFirstMonth({
			coupon: routeSpecificPurchaseParams?.coupon ?? initCouponValue({ coupon: query.coupon, prices, plan }),
			prices,
			isFirstMonthOffer,
			plan,
		}),
		promotion:
			routeSpecificPurchaseParams?.promotion ??
			getPromotion(getParamAsString(query.promotion), isPaidTraffic, isTrialFlow),
		samsungGUID: routeSpecificPurchaseParams?.samsungGUID ?? null,
		guestPass: routeSpecificPurchaseParams?.guestPass ?? getParamAsString(query.guestPass),
		calmjwt: routeSpecificPurchaseParams?.calmjwt ?? getParamAsString(query.calmjwt),
		is30DayOverride:
			routeSpecificPurchaseParams?.is30DayOverride ?? Boolean(getParamAsString(query.is30DayOverride)),
		giftPrice: routeSpecificPurchaseParams?.giftPrice ?? null,
		isUpdateBillingScreen: routeSpecificPurchaseParams?.isUpdateBillingScreen ?? false,
		trialLengthOverride: trialLengthInt, // Only to be used for initialization, use `purchaseType.duration` for logic
	};

	const purchaseType = prices
		? new Purchase({
				...initialPurchaseParams,
				abTests: {},
				prices,
				user,
				isPaidTraffic,
				freeTrialLength: routeSpecificPurchaseParams?.freeTrialLength ?? null,
				hasFreeTrial: routeSpecificPurchaseParams?.hasFreeTrial ?? null,
				freeTrialLengthUnit: routeSpecificPurchaseParams?.freeTrialLengthUnit ?? null,
				noTrial,
			}).purchaseType
		: null;

	return {
		purchaseType,
		...initialPurchaseParams,
	};
}
